import { APP_INITIALIZER, NgModule } from '@angular/core';
import { environment } from '@env/environment';
import {
  Translation,
  TRANSLOCO_CONFIG,
  TRANSLOCO_LOADER,
  TRANSLOCO_MISSING_HANDLER,
  TranslocoMissingHandler,
  translocoConfig,
  TranslocoModule,
  TranslocoService
} from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';

import { TranslocoHttpLoader } from './transloco.http-loader';

/**
 * Many labels are dynamic API values (module/source names) piped through
 * `| transloco`, so they can never all live in the dictionaries. When a key is
 * missing in both the active and fallback language, render the key text as-is
 * and stay silent instead of flooding the console with "Missing translation".
 */
export class SilentMissingHandler implements TranslocoMissingHandler {
  handle(key: string): string {
    return key;
  }
}
@NgModule({
  exports: [TranslocoModule],
  providers: [
    {
      // Provide the default Transloco configuration
      provide: TRANSLOCO_CONFIG,
      useValue: translocoConfig({
        availableLangs: [
          {
            id: 'en',
            label: 'English'
          },
          {
            id: 'zh',
            label: 'Chinese'
          }
        ],
        defaultLang: 'en',
        fallbackLang: 'en',
        // Fall back to the English value for any key missing in the active
        // language (e.g. zh) instead of rendering the raw key, so untranslated
        // strings stay readable and stop logging "missing translation".
        missingHandler: {
          useFallbackTranslation: true,
          allowEmpty: false
        },
        reRenderOnLangChange: true,
        prodMode: environment.production
      })
    },
    {
      // Provide the default Transloco loader
      provide: TRANSLOCO_LOADER,
      useClass: TranslocoHttpLoader
    },
    {
      // Silently render the key for dynamic/unknown labels (no console spam)
      provide: TRANSLOCO_MISSING_HANDLER,
      useClass: SilentMissingHandler
    },
    {
      // Preload the default language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      deps: [TranslocoService],
      useFactory:
        (translocoService: TranslocoService): any =>
        (): Promise<Translation> => {
          const defaultLang = translocoService.getDefaultLang();
          translocoService.setActiveLang(defaultLang);
          return firstValueFrom(translocoService.load(defaultLang));
        },
      multi: true
    }
  ]
})
export class TranslocoCoreModule {}
